/**
 * Loads and searches Google Material Symbols metadata.
 *
 * The metadata file `materialSymbolsMeta.json` is generated from
 *   https://fonts.google.com/metadata/icons?key=material_symbols&incomplete=true
 * and shaped per icon as { n, c, t, p } where:
 *   n = icon name (snake_case identifier used by `material-symbols-outlined`)
 *   c = categories (string[])
 *   t = lowercased, space-separated, deduped tag string for full-text search
 *   p = popularity (higher = more popular)
 *
 * The metadata is loaded once and cached in-module.
 */

export interface IconMeta {
  n: string;
  c: string[];
  t: string;
  p: number;
}

let _icons: IconMeta[] | null = null;
let _categories: string[] | null = null;
let _loadPromise: Promise<IconMeta[]> | null = null;

export async function loadMaterialIcons(): Promise<IconMeta[]> {
  if (_icons) return _icons;
  if (_loadPromise) return _loadPromise;
  _loadPromise = import('../constants/materialSymbolsMeta.json').then((mod) => {
    const data = (mod.default ?? mod) as IconMeta[];
    _icons = data;
    return data;
  });
  return _loadPromise;
}

export function getCategories(): string[] {
  if (_categories) return _categories;
  if (!_icons) return [];
  const set = new Set<string>();
  for (const i of _icons) for (const c of i.c) set.add(c);
  _categories = Array.from(set).sort((a, b) => a.localeCompare(b));
  return _categories;
}

export interface SearchOptions {
  query?: string;
  category?: string | null;
  limit?: number;
}

/**
 * Search icons by name/tag substring with optional category filter.
 * Results are ranked: name-prefix > name-substring > tag-match,
 * then by descending popularity.
 */
export function searchIcons(opts: SearchOptions = {}): IconMeta[] {
  if (!_icons) return [];
  const limit = opts.limit ?? 200;
  const q = (opts.query ?? '').trim().toLowerCase();
  const cat = opts.category ?? null;

  const matches: { icon: IconMeta; score: number }[] = [];
  for (const icon of _icons) {
    if (cat && !icon.c.includes(cat)) continue;

    let score = 0;
    if (!q) {
      score = 1;
    } else if (icon.n === q) {
      score = 1000;
    } else if (icon.n.startsWith(q)) {
      score = 500;
    } else if (icon.n.includes(q)) {
      score = 300;
    } else {
      // tag word-boundary match
      const t = icon.t;
      if (t.includes(q)) {
        // prefer whole-word matches
        const re = new RegExp(`(^|\\s)${escapeRegExp(q)}`);
        score = re.test(t) ? 100 : 50;
      }
    }

    if (score > 0) {
      matches.push({ icon, score });
    }
  }

  matches.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.icon.p - a.icon.p;
  });

  const out: IconMeta[] = [];
  for (let i = 0; i < matches.length && out.length < limit; i++) {
    out.push(matches[i].icon);
  }
  return out;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate / sanitize an icon name string.
 */
export function sanitizeIconName(icon: string | null | undefined, fallback = 'home'): string {
  return icon && /^[a-z0-9_]+$/i.test(icon) ? icon : fallback;
}
